import { useRef, useState, type DragEvent } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import { Upload } from 'lucide-react';
import { globalSnackbar } from 'components/GlobalSnackbar';
import { fileToText, SUPPORTED_EXTENSIONS, UnsupportedFileError } from '../lib/fileToText';
import styles from '../text-compare.module.scss';

type PaneProps = {
  label: string;
  value: string;
  fileName: string | null;
  onChange: (text: string) => void;
  onFile: (text: string, fileName: string) => void;
};

const Pane = ({ label, value, fileName, onChange, onFile }: PaneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file: File) => {
    try {
      const text = await fileToText(file);
      onFile(text, file.name);
    } catch (e) {
      if (e instanceof UnsupportedFileError) {
        globalSnackbar({
          message: `Поддерживаются: ${SUPPORTED_EXTENSIONS.join(', ')}`,
          severity: 'error',
        });
      } else {
        globalSnackbar({ message: 'Не удалось прочитать файл', severity: 'error' });
      }
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  return (
    <div className={styles.inputCell}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'var(--shell-text-secondary)' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {fileName && <Chip size="small" label={fileName} onDelete={() => onFile('', '')} />}
          <Chip
            size="small"
            icon={<Upload size={14} />}
            label="Файл"
            variant="outlined"
            onClick={() => inputRef.current?.click()}
          />
        </Box>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORTED_EXTENSIONS.join(',')}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 'var(--shell-radius-md)',
          outline: dragging ? '2px dashed var(--shell-accent-primary)' : 'none',
          outlineOffset: '-4px',
        }}
      >
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Вставьте текст или перетащите файл (.txt, .md, .docx)"
          multiline
          fullWidth
          sx={{
            height: '100%',
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
              overflow: 'hidden',
            },
            '& textarea': {
              height: '100% !important',
              overflow: 'auto !important',
              fontFamily: 'monospace',
              fontSize: 13,
            },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: 'var(--shell-text-tertiary)', mt: 0.5 }}>
        {value.length} симв.
      </Typography>
    </div>
  );
};

export type CompareInputsProps = {
  left: string;
  right: string;
  leftFile: string | null;
  rightFile: string | null;
  onLeftChange: (text: string) => void;
  onRightChange: (text: string) => void;
  onLeftFile: (text: string, fileName: string) => void;
  onRightFile: (text: string, fileName: string) => void;
};

const CompareInputs = (props: CompareInputsProps) => {
  return (
    <div className={styles.inputs}>
      <Pane
        label="Было"
        value={props.left}
        fileName={props.leftFile}
        onChange={props.onLeftChange}
        onFile={props.onLeftFile}
      />
      <Pane
        label="Стало"
        value={props.right}
        fileName={props.rightFile}
        onChange={props.onRightChange}
        onFile={props.onRightFile}
      />
    </div>
  );
};

export default CompareInputs;
